import { useEffect, useState } from "react";
import {
  DefaultButton,
  Label,
  Panel,
  PanelType,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import { PanelActionOwner, PanelActionType } from "../../enums";
import { FormikTextField } from "../formik-fluent-ui-react/FormikTextField";
import useApi from "../../hooks/useApi";
import { useRecoilState } from "recoil";
import { globalMessageState } from "../../atoms/GlobalMessage";

const schemaReportName = Yup.object().shape({
  name: Yup.string().trim().required("You must enter a report name"),
});

interface IReportNameFormValues {
  name: string;
}

export interface IReportNameEditPanelProps {
  report_id: string;
  name: string;
  isOpen: boolean;
  dismissFunction: () => void;
  successFunction: () => void;
}

export const ReportNameEditPanel = (props: IReportNameEditPanelProps) => {
  const [isopen, setisopen] = useState(false);
  const [, setGlobalMessage] = useRecoilState(globalMessageState);
  const api = useApi();

  useEffect(() => {
    setisopen(props.isOpen);
  }, [props.isOpen]);

  return (
    <Panel
      headerText="Edit Report Name"
      isOpen={isopen}
      onDismiss={() => {
        props.dismissFunction();
      }}
      closeButtonAriaLabel="Close"
      type={PanelType.medium}
      overlayProps={{ isDarkThemed: true }}
    >
      <Formik
        enableReinitialize
        initialValues={{ name: props.name }}
        validationSchema={schemaReportName}
        onSubmit={async (
          values: IReportNameFormValues,
          { setSubmitting }: FormikHelpers<IReportNameFormValues>,
        ) => {
          try {
            await api.put(`/reports/${props.report_id}`, {
              name: values.name.trim(),
            });

            setGlobalMessage({
              action: PanelActionType.Success,
              owner: PanelActionOwner.Report,
              message: "Report name updated successfully",
            });

            props.successFunction();
          } catch (error) {
            console.error(error);

            setGlobalMessage({
              action: PanelActionType.Error,
              owner: PanelActionOwner.Report,
              message: `Error updating report name`,
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isValid, isSubmitting, dirty }) => (
          <Form>
            <Stack
              styles={{
                root: {
                  marginBottom: 12,
                },
              }}
            >
              <Label htmlFor="name" required>
                Report Name
              </Label>
              <Field
                name="name"
                required
                id="name"
                component={FormikTextField}
              />
            </Stack>
            <Stack horizontal horizontalAlign="end">
              <PrimaryButton
                type="submit"
                styles={{ root: { marginRight: 8 } }}
                disabled={!isValid || !dirty || isSubmitting}
                text={isSubmitting ? "Saving..." : "Save"}
              />
              <DefaultButton
                onClick={props.dismissFunction}
                text="Cancel"
                disabled={isSubmitting}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Panel>
  );
};
